// Bundle only what we want from popper
// See: https://popper.js.org/docs#popper-lite-tree-shaking

import {
    defaultModifiers,
    popperGenerator,
} from "@popperjs/core/lib/popper-lite";

import { Instance } from "@popperjs/core/lib/types";

import flip from "@popperjs/core/lib/modifiers/flip";
import offset from "@popperjs/core/lib/modifiers/offset";
import preventOverflow from "@popperjs/core/lib/modifiers/preventOverflow";

const createPopper = popperGenerator({
    defaultModifiers: [...defaultModifiers, flip, offset, preventOverflow],
});

export type Placement =
    | "auto"
    | "auto-start"
    | "auto-end"
    | "top"
    | "top-start"
    | "top-end"
    | "bottom"
    | "bottom-start"
    | "bottom-end"
    | "right"
    | "right-start"
    | "right-end"
    | "left"
    | "left-start"
    | "left-end";

export type Rect = {
    x: number;
    y: number;
    width: number;
    height: number;
};

type OffsetsFunction = (param: {
    popper: Rect;
    reference: Rect;
    placement: Placement;
}) => [number, number];

export interface PopoverOptions {
    distance?: number;
    placement?: Placement;
    skidding?: number;
    strategy?: "absolute" | "fixed";
    offsetFunction?: OffsetsFunction;
    preventOverflow?: boolean;
}

/**
 * Popover wraps PopperJS functionality with sensible defaults.
 */
export default class Popover {
    popper: Instance;
    options: PopoverOptions;

    /**
     * Creates a new instance of the popover
     * @param anchor The element that should get a popover
     * @param popover The content of the popover
     * @param options The options
     */
    constructor(
        anchor: HTMLElement,
        popover: HTMLElement,
        options?: PopoverOptions
    ) {
        this.options = this.mergeWithDefaultOptions(options);
        this.popper = createPopper(anchor, popover, this.getPopperOptions());
    }

    /**
     * Recalculates popover position.
     */
    update() {
        this.popper?.update();
    }

    /**
     * Removes instance data.
     * Does not remove DOM elements.
     */
    destroy() {
        this.popper?.destroy();
    }

    /**
     * Updates the popover options
     */
    setOptions(options: PopoverOptions) {
        this.options = this.mergeWithDefaultOptions(options);
        this.popper?.setOptions(this.getPopperOptions());
    }

    private mergeWithDefaultOptions(options: PopoverOptions) {
        return Object.assign(
            {
                skidding: 0,
                distance: 0,
                placement: "bottom-start",
                strategy: "absolute",
                offsetFunction: null,
                preventOverflow: false,
            },
            options
        );
    }

    private getPopperOptions() {
        return {
            placement: this.options.placement,
            strategy: this.options.strategy,
            modifiers: [
                {
                    name: "flip",
                    options: {
                        boundary: "clippingParents",
                    },
                },
                {
                    name: "offset",
                    options: {
                        offset: this.options.offsetFunction ?? [
                            this.options.skidding,
                            this.options.distance,
                        ],
                    },
                },
                {
                    name: "preventOverflow",
                    enabled: this.options.preventOverflow,
                },
            ],
        };
    }
}
